import { Pipe, PipeTransform } from '@angular/core';

/**
 * Traduz o nome da coluna de `users` (como vem na trilha de edição) para o
 * rótulo que o admin vê na tela de usuário.
 *
 * Coluna sem tradução cai no fallback: `snake_case` vira "Snake Case" — assim
 * uma coluna nova aparece legível no histórico antes mesmo de ser mapeada aqui.
 */
@Pipe({
  name: 'userFieldLabel'
})
export class UserFieldLabelPipe implements PipeTransform {

  private static readonly LABELS: { [field: string]: string } = {
    name: 'Nome',
    email: 'E-mail',
    phone: 'Telefone',
    document: 'CPF/CNPJ',
    password: 'Senha',
    profile_id: 'Perfil',
    active: 'Ativo',
    validated: 'Validado',
    temporary: 'Senha temporária',
    administrator: 'Administrador',
    commission: 'Comissão',
    pix_key: 'Chave Pix',
    pix_priority: 'Prioridade Pix',
    operation_type: 'Tipo de operação',
    unit_cost_sms: 'Custo unitário do SMS',
    login_attempts: 'Tentativas de login',
    reference_id: 'Agência (afiliação)',
    // Campos sintéticos, gravados na trilha da AGÊNCIA quando um cliente entra
    // ou sai da carteira dela. Ver mirrorAffiliation() em UserObserver.
    affiliate_added: 'Afilhado adicionado',
    affiliate_removed: 'Afilhado removido',
    // Ciclo de vida da conta: exclusão e reativação não mudam nenhuma coluna
    // visível na trilha, então entram como campo sintético. Ver deleted() e
    // restored() em UserObserver.
    account_deleted: 'Conta excluída',
    account_restored: 'Conta reativada',
    promotion_id: 'Promoção',
    platform_id: 'Plataforma',
    signature_id: 'Assinatura',
    customer_id: 'Cliente no gateway',
    affiliate_code: 'Código de afiliado',
    call4u_user_id: 'ID do usuário no Call4U',

    shortener_id: 'Encurtador',
    automation_provider_account_id: 'Conta de provedor (automação)',
    broadcast_provider_account_id: 'Conta de provedor (disparo)',
    automation_fractionation_route: 'Rota de fracionamento (automação)',
    broadcast_fractionation_route: 'Rota de fracionamento (disparo)',

    massive_sms_enabled: 'SMS em massa',
    integration_sms_enabled: 'SMS via integração',
    revision_sms_enabled: 'Revisão de conteúdo',
    content_tag_enabled: 'Tag de conteúdo',
    api_enabled: 'API de parceiros',
    multiuser_enabled: 'Multiusuários',
    ai_enabled: 'IA',
    // WhatsApp oficial/Meta e as N integrações (CU-17tm466qj38). O `whatsapp_connect`
    // (WhatsApp antigo por QR) saiu: o backend não grava mais a coluna.
    whatsapp_enabled: 'WhatsApp oficial',
    whatsapp_multi_accounts: 'Várias integrações de WhatsApp',
    max_whatsapp_accounts: 'Limite de integrações de WhatsApp',
    credit_payment_enabled: 'Pagamento por crédito',
    leads_export: 'Exportação de leads',
    sms_token_validated: 'Token SMS validado',
    broadcast_send_interval: 'Intervalo entre disparos',
    fake_request: 'Disparo simulado',
    beta: 'Beta',
    disable_utm_source: 'Desabilitar UTM source',
    analytics_sync: 'Sincronizar analytics',
    analytics_sales_sync: 'Sincronizar vendas do analytics',
    delivery_report_enabled: 'Relatório de entrega',
    funnel_report_enabled: 'Relatório de funil',
    show_funnel_report: 'Exibir relatório de funil',
    show_sales_report: 'Exibir relatório de vendas',
    automacao_novos_cadastros: 'Automação de novos cadastros',
    fully_migrated: 'Migração concluída',
    migration_in_progress: 'Migração em andamento',
    migrated_date: 'Data da migração',

    postcode: 'CEP',
    street: 'Rua',
    number: 'Número',
    complement: 'Complemento',
    district: 'Bairro',
    city: 'Cidade',
    region: 'Estado',
    country: 'País',

    is_billable: 'Emite nota fiscal',
    billing_document: 'CNPJ/CPF de faturamento',
    billing_company_name: 'Razão social',
    billing_company_alias: 'Nome fantasia',
    billing_email: 'E-mail de faturamento',
    billing_im: 'Inscrição municipal',
    billing_postcode: 'CEP de faturamento',
    billing_street: 'Rua de faturamento',
    billing_number: 'Número de faturamento',
    billing_complement: 'Complemento de faturamento',
    billing_district: 'Bairro de faturamento',
    billing_city: 'Cidade de faturamento',
    billing_state: 'Estado de faturamento',
    billing_last_update: 'Última atualização de faturamento',

    account_sid: 'Twilio — Account SID',
    auth_token: 'Twilio — Auth Token',
    twilio_phone: 'Twilio — Telefone',

    api_token: 'Token de API (legado)',

    partner_webhook_url: 'Webhook de parceiro — URL',
    partner_webhook_secret: 'Webhook de parceiro — Segredo',

    two_factor_secret: 'Segundo fator — Segredo',
    two_factor_recovery_codes: 'Segundo fator — Códigos de recuperação',
    two_factor_confirmed_at: 'Segundo fator — Ativado em',
    two_factor_last_timestep: 'Segundo fator — Último código aceito',

    utm_source: 'UTM source',
    average_monthly_invoicing: 'Faturamento médio mensal',
    checkout_provider: 'Provedor de checkout',
  };

  transform(field: string): string {
    if (!field) {
      return '';
    }

    return UserFieldLabelPipe.LABELS[field] ?? this.humanize(field);
  }

  private humanize(field: string): string {
    return field
      .split('_')
      .filter(part => part.length > 0)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
}
